// src/components/CreateTruck.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import {
  FaArrowLeft,
  FaBuilding,
  FaCalendarAlt,
  FaCreditCard,
  FaCube,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaFileAlt,
  FaGasPump,
  FaHashtag,
  FaIdCard,
  FaIndustry,
  FaLayerGroup,
  FaLink,
  FaPlus,
  FaShieldAlt,
  FaTrash,
  FaUpload,
} from "react-icons/fa";

import axios from "../axiosConfig";
import "../styles/CreateTruck.css";

import {
  patchTruckSummaryCaches,
  writeTruckDetailsCache,
} from "../utils/truckDetailsCache";

/* =========================================================
   CONSTANTS
========================================================= */

const EMPTY_FORM_DATA = {
  vin: "",
  type_of_gas: "",
  brand: "",
  model: "",
  plate: "",
  Month: "",
  year: "",

  number_of_straps: "",
  number_of_ratchets: "",
  number_of_edge_protectors: "",
  number_of_anti_slip_mats: "",

  InspectionPhotoExpiry: "",
  TruckInsuranceExpiry: "",
  CommunityLicenceExpiry: "",
};

const EMPTY_FILES = {
  InspectionPhoto: null,
  VehicleRegistrationCertificate: null,
  CommunityLicence: null,
  TruckInsurance: null,
};

const GAS_VALUES = [
  {
    value: "Diesel",
    translationKey: "gas.diesel",
  },
  {
    value: "Compressed Natural Gas (CNG)",
    translationKey: "gas.cng",
  },
  {
    value: "Hybrid",
    translationKey: "gas.hybrid",
  },
  {
    value: "LNG",
    translationKey: "gas.lng",
  },
  {
    value: "Hydrogen",
    translationKey: "gas.hydrogen",
  },
  {
    value: "Electric",
    translationKey: "gas.electric",
  },
];

/* =========================================================
   GENERIC HELPERS
========================================================= */

function normalizePlate(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function extractFileName(value) {
  if (!value) {
    return "";
  }

  if (
    typeof File !== "undefined" &&
    value instanceof File
  ) {
    return value.name || "";
  }

  if (typeof value === "object") {
    return (
      value.name ||
      value.filename ||
      value.originalname ||
      ""
    );
  }

  const clean = String(value)
    .replace(/\\/g, "/")
    .split("?")[0]
    .split("#")[0];

  return clean.split("/").pop() || "";
}

function isPdfFile(value) {
  if (!value) {
    return false;
  }

  if (
    typeof File !== "undefined" &&
    value instanceof File
  ) {
    return (
      value.type === "application/pdf" ||
      /\.pdf$/i.test(value.name || "")
    );
  }

  return /\.pdf$/i.test(extractFileName(value));
}

function isImageFile(value) {
  if (!value) {
    return false;
  }

  if (
    typeof File !== "undefined" &&
    value instanceof File
  ) {
    return (
      String(value.type || "").startsWith("image/") ||
      /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
        value.name || ""
      )
    );
  }

  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
    extractFileName(value)
  );
}

function unwrapCreatedTruck(payload) {
  const candidates = [
    payload?.truck,
    payload?.data?.truck,
    payload?.data,
    payload,
  ];

  return (
    candidates.find((candidate) => {
      return (
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate) &&
        (
          candidate.id !== undefined ||
          candidate.truckId !== undefined ||
          candidate.plate ||
          candidate.vin
        )
      );
    }) || null
  );
}

function findCreatedTruckInSummary(rows, formData) {
  const list = Array.isArray(rows)
    ? rows
    : Array.isArray(rows?.rows)
    ? rows.rows
    : Array.isArray(rows?.items)
    ? rows.items
    : [];

  const wantedVin = String(formData.vin || "")
    .trim()
    .toUpperCase();

  const wantedPlate = normalizePlate(formData.plate);

  return (
    list.find((truck) => {
      const truckVin = String(truck?.vin || "")
        .trim()
        .toUpperCase();

      return wantedVin && truckVin === wantedVin;
    }) ||
    list.find((truck) => {
      return (
        wantedPlate &&
        normalizePlate(truck?.plate) === wantedPlate
      );
    }) ||
    null
  );
}

function createEmptyTruckCard() {
  return {
    clientId:
      `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`,

    provider: "",
    cardNumber: "",
    expiryMonth: "",
    pin: "",
  };
}

function cardErrorKey(
  clientId,
  field
) {
  return `card:${clientId}:${field}`;
}

function hasCardValue(card) {
  return [
    card?.provider,
    card?.cardNumber,
    card?.expiryMonth,
    card?.pin,
  ].some((value) => {
    return (
      String(value ?? "")
        .trim() !== ""
    );
  });
}

/* =========================================================
   REACT SELECT
========================================================= */

function getCreateTruckSelectStyles(hasError) {
  return {
    container: (base) => ({
      ...base,
      width: "100%",
    }),

    control: (base, state) => ({
      ...base,

      width: "100%",
      minHeight: 25,
      height: 25,

      padding: 0,

      border: "none",
      borderRadius: 0,

      backgroundColor: "transparent",
      boxShadow: "none",

      cursor: "pointer",

      opacity: hasError ? 0.96 : 1,

      ":hover": {
        border: "none",
      },
    }),

    valueContainer: (base) => ({
      ...base,

      height: 25,
      padding: 0,
    }),

    input: (base) => ({
      ...base,

      margin: 0,
      padding: 0,

      color: "#f1f3f7",
    }),

    singleValue: (base) => ({
      ...base,

      margin: 0,

      color: "#f1f3f7",

      fontSize: 12,
      fontWeight: 650,
    }),

    placeholder: (base) => ({
      ...base,

      margin: 0,

      color: "#747b87",

      fontSize: 12,
    }),

    indicatorsContainer: (base) => ({
      ...base,

      height: 25,

      display: "flex",
      alignItems: "center",

      transform: "translateY(-2px)",
    }),

    indicatorSeparator: (base) => ({
      ...base,

      height: 18,

      marginTop: 0,
      marginBottom: 0,

      alignSelf: "center",

      backgroundColor: hasError
        ? "rgba(239, 102, 107, 0.45)"
        : "rgba(255, 255, 255, 0.1)",
    }),

    dropdownIndicator: (base, state) => ({
      ...base,

      height: 25,
      padding: "0 6px",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      color: hasError
        ? "#ef777c"
        : state.isFocused
        ? "#b8c3d3"
        : "#858d99",

      ":hover": {
        color: hasError
          ? "#ff9ca0"
          : "#d8dde5",
      },
    }),

    clearIndicator: (base) => ({
      ...base,

      height: 25,
      padding: "0 6px",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      color: "#757d89",

      ":hover": {
        color: "#e88d96",
      },
    }),

    menu: (base) => ({
      ...base,

      zIndex: 9999,

      marginTop: 8,
      overflow: "hidden",

      border:
        "1px solid rgba(255, 255, 255, 0.1)",

      borderRadius: 10,

      backgroundColor: "#111318",

      boxShadow:
        "0 20px 48px rgba(0, 0, 0, 0.48)",
    }),

    menuPortal: (base) => ({
      ...base,

      zIndex: 99999,
    }),

    menuList: (base) => ({
      ...base,

      maxHeight: 250,
      padding: 5,
    }),

    option: (base, state) => ({
      ...base,

      margin: "2px 0",
      padding: "8px 10px",

      borderRadius: 7,

      cursor: "pointer",

      color: state.isSelected
        ? "#ffffff"
        : "#c8ced8",

      backgroundColor: state.isSelected
        ? "rgba(38, 116, 231, 0.28)"
        : state.isFocused
        ? "rgba(255, 255, 255, 0.055)"
        : "transparent",

      fontSize: 11,

      ":active": {
        backgroundColor:
          "rgba(38, 116, 231, 0.22)",
      },
    }),
  };
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function SectionHeading({
  number,
  title,
}) {
  return (
    <div className="create-truck-sectionHeading">
      <h2>
        {number}. {title}
      </h2>
    </div>
  );
}

function FieldCard({
  label,
  icon,
  error,
  className = "",
  children,
}) {
  return (
    <div
      className={[
        "create-truck-fieldCard",
        error ? "has-error" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="create-truck-fieldCard-icon">
        {icon}
      </span>

      <div className="create-truck-fieldCard-body">
        <div className="create-truck-fieldCard-label">
          {label}
        </div>

        <div className="create-truck-fieldCard-control">
          {children}
        </div>

        {error ? (
          <div className="create-truck-fieldCard-error">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DateControl({
  id,
  name,
  value,
  onChange,
  hasError,
}) {
  const inputRef = useRef(null);

  const openCalendar = () => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    input.focus({
      preventScroll: true,
    });

    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
        return;
      }
    } catch {
      // Browser sem suporte a showPicker.
    }

    input.click();
  };

  return (
    <div
      className={[
        "create-truck-dateControl",
        hasError ? "has-error" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="date"
        value={value || ""}
        onChange={onChange}
      />

      <button
        type="button"
        className="create-truck-dateControl-button"
        aria-label="Abrir calendário"
        title="Abrir calendário"
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={openCalendar}
      >
        <FaCalendarAlt />
      </button>
    </div>
  );
}

function MonthControl({
  id,
  value,
  onChange,
  hasError,
}) {
  const emitChange = (
    nextValue
  ) => {
    onChange({
      target: {
        value:
          nextValue,
      },
    });
  };

  return (
    <div
      className={[
        "create-truck-monthControl",
        hasError
          ? "has-error"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={7}
        placeholder="MM-YYYY"
        value={
          value || ""
        }
        onChange={(
          event
        ) => {
          emitChange(
            formatCardExpiryInput(
              event.target.value
            )
          );
        }}
      />

      <input
        type="month"
        className="create-truck-monthControl-nativePicker"
        value={
          cardExpiryToNativeMonth(
            value
          )
        }
        onChange={(
          event
        ) => {
          emitChange(
            nativeMonthToCardExpiry(
              event.target.value
            )
          );
        }}
        tabIndex={-1}
        aria-label="Selecionar mês de validade"
      />

      <span
        className="create-truck-monthControl-button"
        aria-hidden="true"
      >
        <FaCalendarAlt />
      </span>
    </div>
  );
}

function DocumentStatusPill({
  loaded,
  t,
}) {
  return (
    <span
      className={[
        "create-truck-documentStatus",
        loaded
          ? "create-truck-documentStatus--loaded"
          : "create-truck-documentStatus--missing",
      ].join(" ")}
    >
      {loaded
        ? t("documents.status.loaded", {
            defaultValue: "Carregado",
          })
        : t("documents.status.missing", {
            defaultValue: "Em falta",
          })}
    </span>
  );
}

function SidebarDocumentRow({
  title,
  loaded,
  t,
}) {
  return (
    <div className="create-truck-sidebarDocumentRow">
      <div className="create-truck-sidebarDocumentName">
        <FaFileAlt />

        <span>
          {title}
        </span>
      </div>

      <DocumentStatusPill
        loaded={loaded}
        t={t}
      />
    </div>
  );
}

function formatCardExpiryInput(
  value
) {
  const digits =
    String(
      value || ""
    )
      .replace(
        /\D/g,
        ""
      )
      .slice(
        0,
        6
      );

  if (
    digits.length <= 2
  ) {
    return digits;
  }

  return `${digits.slice(
    0,
    2
  )}-${digits.slice(
    2
  )}`;
}

function cardExpiryToNativeMonth(
  value
) {
  const raw =
    String(
      value || ""
    ).trim();

  const match =
    raw.match(
      /^(0[1-9]|1[0-2])-(\d{4})$/
    );

  if (
    !match
  ) {
    return "";
  }

  return `${match[2]}-${match[1]}`;
}

function nativeMonthToCardExpiry(
  value
) {
  const raw =
    String(
      value || ""
    ).trim();

  const match =
    raw.match(
      /^(\d{4})-(0[1-9]|1[0-2])$/
    );

  if (
    !match
  ) {
    return "";
  }

  return `${match[2]}-${match[1]}`;
}

function DocumentCard({
  fieldName,
  title,
  file,
  expiryField,
  expiryValue,
  fileError,
  expiryError,
  onFileChange,
  onDateChange,
  onView,
  t,
}) {
  const inputId = `create-truck-file-${fieldName}`;
  const fileName = extractFileName(file);
  const loaded = Boolean(file);

  const openFilePicker = () => {
    document
      .getElementById(inputId)
      ?.click();
  };

  return (
    <article
      className={[
        "create-truck-documentCard",
        fileError || expiryError
          ? "has-error"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="create-truck-documentCard-header">
        <div className="create-truck-documentCard-identity">
          <span className="create-truck-documentCard-icon">
            <FaFileAlt />
          </span>

          <div className="create-truck-documentCard-titleWrap">
            <h3>
              {title}
            </h3>

            <span
              className="create-truck-documentCard-fileName"
              title={
                fileName ||
                t("file.no_file", {
                  defaultValue: "Sem ficheiro",
                })
              }
            >
              {fileName ||
                t("file.no_file", {
                  defaultValue: "Sem ficheiro",
                })}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="create-truck-documentCard-change"
          onClick={openFilePicker}
        >
          {loaded
            ? t("file.change", {
                defaultValue: "Alterar",
              })
            : t("buttons.upload", {
                defaultValue: "Carregar",
              })}
        </button>
      </header>

      <div className="create-truck-documentCard-middle">
        <DocumentStatusPill
          loaded={loaded}
          t={t}
        />

        {loaded ? (
          <button
            type="button"
            className="create-truck-documentCard-fileLink"
            title={fileName}
            onClick={() => {
              onView({
                file,
                title: fileName || title,
              });
            }}
          >
            <FaFileAlt />

            <span>
              {fileName}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="create-truck-documentCard-empty"
            onClick={openFilePicker}
          >
            <FaUpload />

            <span>
              {t("file.upload_here", {
                defaultValue:
                  "Faz upload do ficheiro aqui.",
              })}
            </span>
          </button>
        )}
      </div>

      <div className="create-truck-documentCard-divider" />

      {expiryField ? (
        <div className="create-truck-documentCard-validity">
          <span>
            {t("labels.expiry_date", {
              defaultValue: "Data de Validade",
            })}
          </span>

          <DateControl
            id={`create-truck-date-${expiryField}`}
            name={expiryField}
            value={expiryValue}
            onChange={onDateChange}
            hasError={Boolean(expiryError)}
          />
        </div>
      ) : (
        <div className="create-truck-documentCard-noExpiry">
          <FaCalendarAlt />

          <span>
            {t("documents.no_expiry", {
              defaultValue:
                "Sem validade associada",
            })}
          </span>
        </div>
      )}

      {fileError ? (
        <div className="create-truck-documentCard-error">
          {fileError}
        </div>
      ) : null}

      {expiryError ? (
        <div className="create-truck-documentCard-error">
          {expiryError}
        </div>
      ) : null}

      <input
        type="file"
        id={inputId}
        name={fieldName}
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={onFileChange}
        className="create-truck-hiddenFileInput"
      />
    </article>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

function CreateTruck() {
  const { t } = useTranslation("create_truck");
  const navigate = useNavigate();

  const [formData, setFormData] = useState(
    EMPTY_FORM_DATA
  );

  const [files, setFiles] = useState(
    EMPTY_FILES
  );

  const [cards, setCards] = useState(
    []
  );

  const [
    visibleCardPins,
    setVisibleCardPins,
  ] = useState({});

  const [invalidFields, setInvalidFields] = useState(
    {}
  );

  const [
    vinDuplicateError,
    setVinDuplicateError,
  ] = useState(false);

  const [
    plateDuplicateError,
    setPlateDuplicateError,
  ] = useState(false);

  const [
    vinLengthError,
    setVinLengthError,
  ] = useState(false);

  const [
    globalError,
    setGlobalError,
  ] = useState("");

  const [
    showSuccessPopup,
    setShowSuccessPopup,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    viewerFile,
    setViewerFile,
  ] = useState(null);

  const navigationTimerRef = useRef(null);

  const gasOptions = useMemo(() => {
    return GAS_VALUES.map((option) => ({
      value: option.value,

      label: t(option.translationKey, {
        defaultValue: option.value,
      }),
    }));
  }, [t]);

  const monthOptions = useMemo(() => {
    return Array.from(
      {
        length: 12,
      },
      (_, index) => ({
        value: index + 1,

        label: t(`months.${index + 1}`, {
          defaultValue: String(index + 1),
        }),
      })
    );
  }, [t]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 1980;

    return Array.from(
      {
        length:
          currentYear -
          startYear +
          1,
      },
      (_, index) => {
        const year =
          currentYear -
          index;

        return {
          value: year,
          label: String(year),
        };
      }
    );
  }, []);

  const selectedGas = useMemo(() => {
    return (
      gasOptions.find((option) => {
        return (
          option.value ===
          formData.type_of_gas
        );
      }) || null
    );
  }, [
    gasOptions,
    formData.type_of_gas,
  ]);

  const selectedMonth = useMemo(() => {
    return (
      monthOptions.find((option) => {
        return (
          Number(option.value) ===
          Number(formData.Month)
        );
      }) || null
    );
  }, [
    monthOptions,
    formData.Month,
  ]);

  const selectedYear = useMemo(() => {
    return (
      yearOptions.find((option) => {
        return (
          Number(option.value) ===
          Number(formData.year)
        );
      }) || null
    );
  }, [
    yearOptions,
    formData.year,
  ]);

  const clearFieldError = useCallback((field) => {
    setInvalidFields((previous) => {
      if (!previous[field]) {
        return previous;
      }

      const next = {
        ...previous,
      };

      delete next[field];

      return next;
    });
  }, []);

  const updateField = useCallback(
    (field, value) => {
      setFormData((previous) => ({
        ...previous,
        [field]: value,
      }));

      clearFieldError(field);
      setGlobalError("");

      if (field === "vin") {
        const cleanVin = String(value || "")
          .trim();

        setVinLengthError(
          cleanVin !== "" &&
          cleanVin.length !== 17
        );
      }
    },
    [clearFieldError]
  );

  const handleChange = useCallback(
    (event) => {
      const {
        name,
        value,
      } = event.target;

      updateField(
        name,
        name === "plate"
          ? normalizePlate(value)
          : value
      );
    },
    [updateField]
  );

  const handleFileChange = useCallback(
    (event) => {
      const {
        name,
        files: uploadedFiles,
      } = event.target;

      const selectedFile =
        uploadedFiles?.[0] || null;

      if (!selectedFile) {
        return;
      }

      setFiles((previous) => ({
        ...previous,
        [name]: selectedFile,
      }));

      clearFieldError(name);
      setGlobalError("");
    },
    [clearFieldError]
  );

  const addCard = useCallback(() => {
    setCards((previous) => [
      ...previous,
      createEmptyTruckCard(),
    ]);
  }, []);

  const removeCard = useCallback(
    (clientId) => {
      setCards((previous) => {
        return previous.filter(
          (card) => {
            return (
              card.clientId !==
              clientId
            );
          }
        );
      });

      setVisibleCardPins(
        (previous) => {
          const next = {
            ...previous,
          };

          delete next[clientId];

          return next;
        }
      );

      setInvalidFields(
        (previous) => {
          const next = {
            ...previous,
          };

          Object.keys(next)
            .filter((key) => {
              return key.startsWith(
                `card:${clientId}:`
              );
            })
            .forEach((key) => {
              delete next[key];
            });

          return next;
        }
      );
    },
    []
  );

  const updateCard = useCallback(
    (
      clientId,
      field,
      value
    ) => {
      setCards((previous) => {
        return previous.map(
          (card) => {
            if (
              card.clientId !==
              clientId
            ) {
              return card;
            }

            return {
              ...card,
              [field]: value,
            };
          }
        );
      });

      clearFieldError(
        cardErrorKey(
          clientId,
          field
        )
      );

      setGlobalError("");
    },
    [clearFieldError]
  );

  const toggleCardPin = useCallback(
    (clientId) => {
      setVisibleCardPins(
        (previous) => ({
          ...previous,

          [clientId]:
            !previous[clientId],
        })
      );
    },
    []
  );

  const checkVinExists = useCallback(
    async (vin, signal) => {
      const response = await axios.get(
        "/trucks/check-vin",
        {
          params: {
            vin,
          },
          signal,
        }
      );

      return Boolean(response.data?.exists);
    },
    []
  );

  const checkPlateExists = useCallback(
    async (plate, signal) => {
      const response = await axios.get(
        "/trucks/check-plate",
        {
          params: {
            plate,
          },
          signal,
        }
      );

      return Boolean(response.data?.exists);
    },
    []
  );

  useEffect(() => {
    const cleanVin = String(formData.vin || "")
      .trim();

    if (cleanVin.length !== 17) {
      setVinDuplicateError(false);
      return undefined;
    }

    const controller = new AbortController();

    const timeout = window.setTimeout(async () => {
      try {
        const exists = await checkVinExists(
          cleanVin,
          controller.signal
        );

        setVinDuplicateError(exists);
      } catch (error) {
        if (
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error(
          "[CreateTruck] VIN uniqueness check failed:",
          error
        );
      }
    }, 400);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [
    formData.vin,
    checkVinExists,
  ]);

  useEffect(() => {
    const cleanPlate = normalizePlate(
      formData.plate
    );

    if (cleanPlate.length < 3) {
      setPlateDuplicateError(false);
      return undefined;
    }

    const controller = new AbortController();

    const timeout = window.setTimeout(async () => {
      try {
        const exists = await checkPlateExists(
          cleanPlate,
          controller.signal
        );

        setPlateDuplicateError(exists);
      } catch (error) {
        if (
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error(
          "[CreateTruck] plate uniqueness check failed:",
          error
        );
      }
    }, 400);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [
    formData.plate,
    checkPlateExists,
  ]);

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        window.clearTimeout(
          navigationTimerRef.current
        );
      }
    };
  }, []);

  const validateFields = useCallback(() => {
    const nextErrors = {};

    const cleanVin = String(formData.vin || "")
      .trim();

    if (
      cleanVin !== "" &&
      cleanVin.length !== 17
    ) {
      nextErrors.vin = t("errors.vin_length", {
        defaultValue:
          "O VIN deve ter exatamente 17 caracteres.",
      });
    } else if (vinDuplicateError) {
      nextErrors.vin = t("errors.vin_exists", {
        defaultValue: "O VIN já existe.",
      });
    }

    if (plateDuplicateError) {
      nextErrors.plate = t(
        "errors.plate_exists",
        {
          defaultValue:
            "A matrícula já existe.",
        }
      );
    }

    cards.forEach((card) => {
      if (!hasCardValue(card)) {
        return;
      }

      if (
        !String(
          card.provider || ""
        ).trim()
      ) {
        nextErrors[
          cardErrorKey(
            card.clientId,
            "provider"
          )
        ] = t(
          "errors.card_provider_required",
          {
            defaultValue:
              "Indique o fornecedor do cartão.",
          }
        );
      }

      if (
        !String(
          card.cardNumber || ""
        ).trim()
      ) {
        nextErrors[
          cardErrorKey(
            card.clientId,
            "cardNumber"
          )
        ] = t(
          "errors.card_number_required",
          {
            defaultValue:
              "Indique o número do cartão.",
          }
        );
      }
    });

    setInvalidFields(nextErrors);

    return (
      Object.keys(nextErrors).length === 0
    );
  }, [
    formData.vin,
    vinDuplicateError,
    plateDuplicateError,
    cards,
    t,
  ]);

  const updateTruckCaches = useCallback(
    async (responseData) => {
      let createdTruck = unwrapCreatedTruck(
        responseData
      );

      const returnedId =
        createdTruck?.id ??
        createdTruck?.truckId ??
        responseData?.id ??
        responseData?.truckId ??
        responseData?.data?.id ??
        responseData?.data?.truckId ??
        null;

      if (createdTruck && returnedId) {
        createdTruck = {
          ...createdTruck,
          id: returnedId,
        };
      }

      if (!createdTruck?.id) {
        try {
          const summaryResponse = await axios.get(
            "/trucks/summary"
          );

          createdTruck = findCreatedTruckInSummary(
            summaryResponse.data,
            formData
          );
        } catch (error) {
          console.warn(
            "[CreateTruck] truck created but summary cache could not be primed:",
            error
          );
        }
      }

      if (!createdTruck?.id) {
        return;
      }

      const cachedTruck = {
        ...formData,
        ...createdTruck,

        id: createdTruck.id,

        plate:
          createdTruck.plate ||
          normalizePlate(formData.plate),

        Month:
          createdTruck.Month ??
          createdTruck.month ??
          formData.Month,

        month:
          createdTruck.month ??
          createdTruck.Month ??
          formData.Month,

        status:
          createdTruck.status ||
          createdTruck.Status ||
          "ACTIVE",
      };

      writeTruckDetailsCache(
        createdTruck.id,
        cachedTruck
      );

      patchTruckSummaryCaches(
        cachedTruck
      );
    },
    [formData]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSaving) {
      return;
    }

    setGlobalError("");
    setShowSuccessPopup(false);

    const isValid = validateFields();

    if (!isValid) {
      setGlobalError(
        t("errors.fill_all", {
          defaultValue:
            "Verifica os campos assinalados.",
        })
      );

      return;
    }

    setIsSaving(true);

    try {
      const cleanVin = String(formData.vin || "")
        .trim();

      if (cleanVin.length === 17) {
        const exists = await checkVinExists(
          cleanVin
        );

        if (exists) {
          setVinDuplicateError(true);

          setInvalidFields((previous) => ({
            ...previous,

            vin: t("errors.vin_exists", {
              defaultValue:
                "O VIN já existe.",
            }),
          }));

          return;
        }
      }

      const cleanPlate = normalizePlate(
        formData.plate
      );

      if (cleanPlate.length >= 3) {
        const exists = await checkPlateExists(
          cleanPlate
        );

        if (exists) {
          setPlateDuplicateError(true);

          setInvalidFields((previous) => ({
            ...previous,

            plate: t("errors.plate_exists", {
              defaultValue:
                "A matrícula já existe.",
            }),
          }));

          return;
        }
      }

      const payload = new FormData();

      Object.entries(formData).forEach(
        ([key, value]) => {
          const normalizedValue =
            typeof value === "string"
              ? value.trim()
              : value;

          if (
            normalizedValue !== "" &&
            normalizedValue !== null &&
            normalizedValue !== undefined
          ) {
            payload.append(
              key,
              normalizedValue
            );
          }
        }
      );

      const cardsToSubmit =
        cards
          .filter(hasCardValue)
          .map(
            ({
              clientId,
              ...card
            }) => card
          );

      if (
        cardsToSubmit.length > 0
      ) {
        payload.append(
          "cards",
          JSON.stringify(
            cardsToSubmit
          )
        );
      }

      Object.entries(files).forEach(
        ([key, file]) => {
          if (file) {
            payload.append(
              key,
              file
            );
          }
        }
      );

      const cardsPayload =
        cards
          .filter(
            (
              card
            ) =>
              hasCardValue(
                card
              )
          )
          .map(
            (
              card
            ) => ({
              provider:
                String(
                  card.provider ||
                    ""
                ).trim(),

              cardNumber:
                String(
                  card.cardNumber ||
                    ""
                ).trim(),

              expiryMonth:
                String(
                  card.expiryMonth ||
                    ""
                ).trim(),

              pin:
                String(
                  card.pin ||
                    ""
                ).trim(),
            })
          );

      payload.append(
        "cards",
        JSON.stringify(
          cardsPayload
        )
      );

      const response = await axios.post(
        "/trucks",
        payload,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      await updateTruckCaches(
        response.data
      );

      setShowSuccessPopup(true);

      navigationTimerRef.current =
        window.setTimeout(() => {
          navigate("/trucks");
        }, 1200);
    } catch (error) {
      console.error(
        "[CreateTruck] create failed:",
        error
      );

      const backendField =
        error?.response?.data?.field;

      if (backendField === "vin") {
        setVinDuplicateError(true);

        setInvalidFields((previous) => ({
          ...previous,

          vin:
            error?.response?.data?.message ||
            t("errors.vin_exists", {
              defaultValue:
                "O VIN já existe.",
            }),
        }));

        return;
      }

      if (backendField === "plate") {
        setPlateDuplicateError(true);

        setInvalidFields((previous) => ({
          ...previous,

          plate:
            error?.response?.data?.message ||
            t("errors.plate_exists", {
              defaultValue:
                "A matrícula já existe.",
            }),
        }));

        return;
      }

      setGlobalError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("errors.create_fail", {
          defaultValue:
            "Falha ao criar o camião. Tenta novamente.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const viewerUrl = useMemo(() => {
    if (!viewerFile?.file) {
      return "";
    }

    try {
      return URL.createObjectURL(
        viewerFile.file
      );
    } catch {
      return "";
    }
  }, [viewerFile]);

  useEffect(() => {
    return () => {
      if (viewerUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(viewerUrl);
        } catch {
          // URL já revogado.
        }
      }
    };
  }, [viewerUrl]);

  useEffect(() => {
    if (!viewerFile) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setViewerFile(null);
      }
    };

    window.addEventListener(
      "keydown",
      onKeyDown
    );

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      window.removeEventListener(
        "keydown",
        onKeyDown
      );

      document.body.style.overflow =
        previousOverflow;
    };
  }, [viewerFile]);

  const documentItems = [
    {
      key: "InspectionPhoto",

      title: t("labels.inspection_photo", {
        defaultValue:
          "Inspeção (Foto/Doc.)",
      }),

      expiryField:
        "InspectionPhotoExpiry",

      expiryValue:
        formData.InspectionPhotoExpiry,
    },

    {
      key: "TruckInsurance",

      title: t("labels.truck_insurance", {
        defaultValue:
          "Seguro do camião",
      }),

      expiryField:
        "TruckInsuranceExpiry",

      expiryValue:
        formData.TruckInsuranceExpiry,
    },

    {
      key: "CommunityLicence",

      title: t("labels.community_licence", {
        defaultValue:
          "Licença Comunitária",
      }),

      expiryField:
        "CommunityLicenceExpiry",

      expiryValue:
        formData.CommunityLicenceExpiry,
    },

    {
      key:
        "VehicleRegistrationCertificate",

      title: t(
        "labels.vehicle_registration_certificate",
        {
          defaultValue:
            "Certificado de Matrícula",
        }
      ),

      expiryField: null,
      expiryValue: "",
    },
  ].map((item) => ({
    ...item,
    file: files[item.key],
  }));

  const selectedGasLabel =
    selectedGas?.label || "—";

  const selectedMonthLabel =
    selectedMonth?.label || "—";

  const brandModel = [
    formData.brand,
    formData.model,
  ]
    .filter(Boolean)
    .join(" ") || "—";

  const sidebarSpecs = [
    {
      label: t("labels.year", {
        defaultValue: "Ano",
      }),

      value: formData.year || "—",
    },

    {
      label: t("labels.month", {
        defaultValue: "Mês",
      }),

      value: selectedMonthLabel,
    },

    {
      label: t("labels.number_of_straps", {
        defaultValue:
          "N.º de Cintas",
      }),

      value:
        formData.number_of_straps ||
        "0",
    },

    {
      label: t(
        "labels.number_of_ratchets",
        {
          defaultValue:
            "N.º de Catracas",
        }
      ),

      value:
        formData.number_of_ratchets ||
        "0",
    },

    {
      label: t(
        "labels.number_of_edge_protectors",
        {
          defaultValue:
            "N.º de Protetores de Canto",
        }
      ),

      value:
        formData.number_of_edge_protectors ||
        "0",
    },

    {
      label: t(
        "labels.number_of_anti_slip_mats",
        {
          defaultValue:
            "N.º de Tapetes Antiderrapantes",
        }
      ),

      value:
        formData.number_of_anti_slip_mats ||
        "0",
    },
  ];

  return (
    <div className="create-truck-page">
      <div className="create-truck-shell">
        <header className="create-truck-topbar">
          <button
            type="button"
            className="create-truck-backButton"
            onClick={() => navigate("/trucks")}
          >
            <FaArrowLeft />

            <span>
              {t("back", {
                defaultValue: "Voltar",
              })}
            </span>
          </button>

          <div className="create-truck-topbar-title">
            <h1>
              {t("title", {
                defaultValue:
                  "Criar camião",
              })}
            </h1>

            <p>
              {t("page.subtitle", {
                defaultValue:
                  "Registe os dados, documentos e equipamento do novo camião.",
              })}
            </p>
          </div>

          <div className="create-truck-topbar-actions">
            <button
              type="button"
              className="create-truck-cancelButton"
              disabled={isSaving}
              onClick={() => navigate("/trucks")}
            >
              {t("buttons.cancel", {
                defaultValue: "Cancelar",
              })}
            </button>

            <button
              type="submit"
              form="create-truck-form"
              className="create-truck-primaryButton"
              disabled={isSaving}
            >
              <FaPlus />

              <span>
                {isSaving
                  ? t("buttons.creating", {
                      defaultValue:
                        "A criar...",
                    })
                  : t("buttons.create_truck", {
                      defaultValue:
                        "Criar Camião",
                    })}
              </span>
            </button>
          </div>
        </header>

        {globalError ? (
          <div className="create-truck-globalError">
            <FaExclamationTriangle />

            <span>
              {globalError}
            </span>
          </div>
        ) : null}

        <div className="create-truck-layout">
          <aside className="create-truck-sidebar">
            <div className="create-truck-sidebarCard">
              <div className="create-truck-sidebarIdentity">
                <span className="create-truck-sidebarEyebrow">
                  {t("labels.plate", {
                    defaultValue:
                      "Matrícula",
                  })}
                </span>

                <h2>
                  {formData.plate || "—"}
                </h2>

                <p>
                  {brandModel}
                </p>

                <span className="create-truck-newStatus">
                  <span />

                  {t("status.new", {
                    defaultValue:
                      "Novo registo",
                  })}
                </span>
              </div>

              <div className="create-truck-sidebarBlock">
                <div className="create-truck-sidebarBlockTitle">
                  {t("sections.fuel", {
                    defaultValue:
                      "Combustível",
                  })}
                </div>

                <div className="create-truck-sidebarFuel">
                  <FaGasPump />

                  <span>
                    {selectedGasLabel}
                  </span>
                </div>
              </div>

              <div className="create-truck-sidebarBlock">
                <div className="create-truck-sidebarBlockTitle">
                  {t("sections.documents", {
                    defaultValue:
                      "Documentos",
                  })}
                </div>

                <div className="create-truck-sidebarDocuments">
                  {documentItems.map((item) => (
                    <SidebarDocumentRow
                      key={item.key}
                      title={item.title}
                      loaded={Boolean(item.file)}
                      t={t}
                    />
                  ))}
                </div>
              </div>

              <div className="create-truck-sidebarBlock">
                <div className="create-truck-sidebarBlockTitle">
                  {t(
                    "sections.specifications",
                    {
                      defaultValue:
                        "Especificações",
                    }
                  )}
                </div>

                <div className="create-truck-sidebarSpecs">
                  {sidebarSpecs.map((item) => (
                    <div
                      key={item.label}
                      className="create-truck-sidebarSpecRow"
                    >
                      <span>
                        {item.label}
                      </span>

                      <strong>
                        {item.value}
                      </strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <main className="create-truck-main">
            <form
              id="create-truck-form"
              className="create-truck-form"
              onSubmit={handleSubmit}
              encType="multipart/form-data"
            >
              <section className="create-truck-sectionCard">
                <SectionHeading
                  number="1"
                  title={t("sections.basic", {
                    defaultValue:
                      "Informação Básica",
                  })}
                />

                <div className="create-truck-basicGrid">
                  <FieldCard
                    label="VIN"
                    icon={<FaHashtag />}
                    error={invalidFields.vin}
                    className="create-truck-span-7"
                  >
                    <input
                      name="vin"
                      value={formData.vin}
                      onChange={handleChange}
                      type="text"
                      maxLength={17}
                      className="create-truck-controlInput create-truck-controlInput--uppercase"
                      placeholder={t(
                        "placeholders.vin",
                        {
                          defaultValue:
                            "Introduz o VIN...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.type_of_gas",
                      {
                        defaultValue:
                          "Tipo de Combustível",
                      }
                    )}
                    icon={<FaGasPump />}
                    error={
                      invalidFields.type_of_gas
                    }
                    className="create-truck-span-5"
                  >
                    <Select
                      name="type_of_gas"
                      value={selectedGas}
                      options={gasOptions}
                      onChange={(option) => {
                        updateField(
                          "type_of_gas",
                          option?.value || ""
                        );
                      }}
                      placeholder={t(
                        "placeholders.type_of_gas",
                        {
                          defaultValue:
                            "Seleciona o tipo",
                        }
                      )}
                      styles={getCreateTruckSelectStyles(
                        Boolean(
                          invalidFields.type_of_gas
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t("labels.brand", {
                      defaultValue: "Marca",
                    })}
                    icon={<FaIndustry />}
                    error={invalidFields.brand}
                    className="create-truck-span-4"
                  >
                    <input
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      type="text"
                      className="create-truck-controlInput"
                      placeholder={t(
                        "placeholders.brand",
                        {
                          defaultValue:
                            "Introduz a marca...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t("labels.model", {
                      defaultValue: "Modelo",
                    })}
                    icon={<FaCube />}
                    error={invalidFields.model}
                    className="create-truck-span-4"
                  >
                    <input
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      type="text"
                      className="create-truck-controlInput"
                      placeholder={t(
                        "placeholders.model",
                        {
                          defaultValue:
                            "Introduz o modelo...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t("labels.plate", {
                      defaultValue:
                        "Matrícula",
                    })}
                    icon={<FaIdCard />}
                    error={
                      invalidFields.plate
                    }
                    className="create-truck-span-4"
                  >
                    <input
                      name="plate"
                      value={formData.plate}
                      onChange={handleChange}
                      type="text"
                      className="create-truck-controlInput create-truck-controlInput--uppercase"
                      placeholder={t(
                        "placeholders.plate",
                        {
                          defaultValue:
                            "Introduz a matrícula...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t("labels.month", {
                      defaultValue: "Mês",
                    })}
                    icon={<FaCalendarAlt />}
                    error={invalidFields.Month}
                    className="create-truck-span-6"
                  >
                    <Select
                      name="Month"
                      value={selectedMonth}
                      options={monthOptions}
                      onChange={(option) => {
                        updateField(
                          "Month",
                          option?.value || ""
                        );
                      }}
                      placeholder={t(
                        "placeholders.month",
                        {
                          defaultValue:
                            "Seleciona o mês",
                        }
                      )}
                      styles={getCreateTruckSelectStyles(
                        Boolean(
                          invalidFields.Month
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t("labels.year", {
                      defaultValue: "Ano",
                    })}
                    icon={<FaCalendarAlt />}
                    error={invalidFields.year}
                    className="create-truck-span-6"
                  >
                    <Select
                      name="year"
                      value={selectedYear}
                      options={yearOptions}
                      onChange={(option) => {
                        updateField(
                          "year",
                          option?.value || ""
                        );
                      }}
                      placeholder={t(
                        "placeholders.year",
                        {
                          defaultValue:
                            "Seleciona o ano",
                        }
                      )}
                      styles={getCreateTruckSelectStyles(
                        Boolean(
                          invalidFields.year
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_straps",
                      {
                        defaultValue:
                          "N.º de Cintas",
                      }
                    )}
                    icon={<FaLink />}
                    error={
                      invalidFields.number_of_straps
                    }
                    className="create-truck-span-3"
                  >
                    <input
                      name="number_of_straps"
                      value={
                        formData.number_of_straps
                      }
                      onChange={handleChange}
                      type="number"
                      min="0"
                      className="create-truck-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_ratchets",
                      {
                        defaultValue:
                          "N.º de Catracas",
                      }
                    )}
                    icon={<FaLink />}
                    error={
                      invalidFields.number_of_ratchets
                    }
                    className="create-truck-span-3"
                  >
                    <input
                      name="number_of_ratchets"
                      value={
                        formData.number_of_ratchets
                      }
                      onChange={handleChange}
                      type="number"
                      min="0"
                      className="create-truck-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_edge_protectors",
                      {
                        defaultValue:
                          "N.º de Protetores de Canto",
                      }
                    )}
                    icon={<FaShieldAlt />}
                    error={
                      invalidFields.number_of_edge_protectors
                    }
                    className="create-truck-span-3"
                  >
                    <input
                      name="number_of_edge_protectors"
                      value={
                        formData.number_of_edge_protectors
                      }
                      onChange={handleChange}
                      type="number"
                      min="0"
                      className="create-truck-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_anti_slip_mats",
                      {
                        defaultValue:
                          "N.º de Tapetes Antiderrapantes",
                      }
                    )}
                    icon={<FaLayerGroup />}
                    error={
                      invalidFields.number_of_anti_slip_mats
                    }
                    className="create-truck-span-3"
                  >
                    <input
                      name="number_of_anti_slip_mats"
                      value={
                        formData.number_of_anti_slip_mats
                      }
                      onChange={handleChange}
                      type="number"
                      min="0"
                      className="create-truck-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>
                </div>
              </section>

              <section className="create-truck-sectionCard">
                <SectionHeading
                  number="2"
                  title={t(
                    "sections.cards",
                    {
                      defaultValue:
                        "Cartões",
                    }
                  )}
                />

                <div className="create-truck-cardsHeader">
                  <p className="create-truck-cardsHint">
                    {t(
                      "cards.security_note",
                      {
                        defaultValue:
                          "Associe cartões de combustível, frota ou serviço ao camião. Não guarde PINs de cartões bancários.",
                      }
                    )}
                  </p>

                  <button
                    type="button"
                    className="create-truck-addCardButton"
                    onClick={addCard}
                  >
                    <FaPlus />

                    <span>
                      {t(
                        "buttons.add_card",
                        {
                          defaultValue:
                            "Adicionar cartão",
                        }
                      )}
                    </span>
                  </button>
                </div>

                {cards.length > 0 ? (
                  <div className="create-truck-cardsList">
                    {cards.map(
                      (card, index) => {
                        const pinVisible =
                          Boolean(
                            visibleCardPins[
                              card.clientId
                            ]
                          );

                        return (
                          <article
                            key={
                              card.clientId
                            }
                            className="create-truck-cardItem"
                          >
                            <header className="create-truck-cardItemHeader">
                              <div className="create-truck-cardItemTitle">
                                <FaCreditCard />

                                <span>
                                  {t(
                                    "cards.card_number",
                                    {
                                      number:
                                        index + 1,

                                      defaultValue:
                                        `Cartão ${
                                          index + 1
                                        }`,
                                    }
                                  )}
                                </span>
                              </div>

                              <button
                                type="button"
                                className="create-truck-removeCardButton"
                                onClick={() => {
                                  removeCard(
                                    card.clientId
                                  );
                                }}
                                aria-label={t(
                                  "buttons.remove_card",
                                  {
                                    defaultValue:
                                      "Remover cartão",
                                  }
                                )}
                                title={t(
                                  "buttons.remove_card",
                                  {
                                    defaultValue:
                                      "Remover cartão",
                                  }
                                )}
                              >
                                <FaTrash />
                              </button>
                            </header>

                            <div className="create-truck-cardGrid">
                              <FieldCard
                                label={t(
                                  "labels.card_provider",
                                  {
                                    defaultValue:
                                      "Fornecedor",
                                  }
                                )}
                                icon={
                                  <FaBuilding />
                                }
                                error={
                                  invalidFields[
                                    cardErrorKey(
                                      card.clientId,
                                      "provider"
                                    )
                                  ]
                                }
                                className="create-truck-cardSpan-3"
                              >
                                <input
                                  type="text"
                                  value={
                                    card.provider
                                  }
                                  onChange={(
                                    event
                                  ) => {
                                    updateCard(
                                      card.clientId,
                                      "provider",
                                      event.target
                                        .value
                                    );
                                  }}
                                  className="create-truck-controlInput"
                                  placeholder={t(
                                    "placeholders.card_provider",
                                    {
                                      defaultValue:
                                        "Ex.: DKV, UTA, Shell...",
                                    }
                                  )}
                                  maxLength={80}
                                  autoComplete="off"
                                />
                              </FieldCard>

                              <FieldCard
                                label={t(
                                  "labels.card_number",
                                  {
                                    defaultValue:
                                      "N.º do cartão",
                                  }
                                )}
                                icon={
                                  <FaCreditCard />
                                }
                                error={
                                  invalidFields[
                                    cardErrorKey(
                                      card.clientId,
                                      "cardNumber"
                                    )
                                  ]
                                }
                                className="create-truck-cardSpan-4"
                              >
                                <input
                                  type="text"
                                  value={
                                    card.cardNumber
                                  }
                                  onChange={(
                                    event
                                  ) => {
                                    updateCard(
                                      card.clientId,
                                      "cardNumber",
                                      event.target
                                        .value
                                    );
                                  }}
                                  className="create-truck-controlInput"
                                  placeholder={t(
                                    "placeholders.card_number",
                                    {
                                      defaultValue:
                                        "Introduza o número...",
                                    }
                                  )}
                                  maxLength={64}
                                  autoComplete="off"
                                  spellCheck="false"
                                />
                              </FieldCard>

                              <FieldCard
                                label={t(
                                  "labels.card_expiry",
                                  {
                                    defaultValue:
                                      "Validade",
                                  }
                                )}
                                icon={
                                  <FaCalendarAlt />
                                }
                                className="create-truck-cardSpan-3"
                              >
                                <MonthControl
                                  id={`create-truck-card-expiry-${card.clientId}`}
                                  value={
                                    card.expiryMonth
                                  }
                                  onChange={(
                                    event
                                  ) => {
                                    updateCard(
                                      card.clientId,
                                      "expiryMonth",
                                      event.target
                                        .value
                                    );
                                  }}
                                  hasError={
                                    false
                                  }
                                />
                              </FieldCard>

                              <FieldCard
                                label={t(
                                  "labels.card_pin",
                                  {
                                    defaultValue:
                                      "PIN / código",
                                  }
                                )}
                                icon={
                                  <FaShieldAlt />
                                }
                                className="create-truck-cardSpan-2"
                              >
                                <div className="create-truck-cardPinControl">
                                  <input
                                    type={
                                      pinVisible
                                        ? "text"
                                        : "password"
                                    }
                                    value={
                                      card.pin
                                    }
                                    onChange={(
                                      event
                                    ) => {
                                      updateCard(
                                        card.clientId,
                                        "pin",
                                        event.target
                                          .value
                                      );
                                    }}
                                    className="create-truck-controlInput"
                                    placeholder={t(
                                      "placeholders.card_pin",
                                      {
                                        defaultValue:
                                          "••••",
                                      }
                                    )}
                                    maxLength={64}
                                    autoComplete="new-password"
                                    spellCheck="false"
                                  />

                                  <button
                                    type="button"
                                    className="create-truck-cardPinToggle"
                                    onClick={() => {
                                      toggleCardPin(
                                        card.clientId
                                      );
                                    }}
                                    aria-label={
                                      pinVisible
                                        ? t(
                                            "cards.hide_pin",
                                            {
                                              defaultValue:
                                                "Ocultar PIN",
                                            }
                                          )
                                        : t(
                                            "cards.show_pin",
                                            {
                                              defaultValue:
                                                "Mostrar PIN",
                                            }
                                          )
                                    }
                                    title={
                                      pinVisible
                                        ? t(
                                            "cards.hide_pin",
                                            {
                                              defaultValue:
                                                "Ocultar PIN",
                                            }
                                          )
                                        : t(
                                            "cards.show_pin",
                                            {
                                              defaultValue:
                                                "Mostrar PIN",
                                            }
                                          )
                                    }
                                  >
                                    {pinVisible ? (
                                      <FaEyeSlash />
                                    ) : (
                                      <FaEye />
                                    )}
                                  </button>
                                </div>
                              </FieldCard>
                            </div>
                          </article>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <div className="create-truck-cardsEmpty">
                    <FaCreditCard />

                    <span>
                      {t(
                        "cards.empty",
                        {
                          defaultValue:
                            "Nenhum cartão associado.",
                        }
                      )}
                    </span>
                  </div>
                )}
              </section>

              <section className="create-truck-sectionCard">
                <SectionHeading
                  number="3"
                  title={t(
                    "sections.documents_validities",
                    {
                      defaultValue:
                        "Documentos & Validades",
                    }
                  )}
                />

                <div className="create-truck-documentsGrid">
                  {documentItems.map((item) => (
                    <DocumentCard
                      key={item.key}
                      fieldName={item.key}
                      title={item.title}
                      file={item.file}
                      expiryField={
                        item.expiryField
                      }
                      expiryValue={
                        item.expiryValue
                      }
                      fileError={
                        invalidFields[item.key]
                      }
                      expiryError={
                        item.expiryField
                          ? invalidFields[
                              item.expiryField
                            ]
                          : null
                      }
                      onFileChange={
                        handleFileChange
                      }
                      onDateChange={
                        handleChange
                      }
                      onView={setViewerFile}
                      t={t}
                    />
                  ))}
                </div>
              </section>
            </form>
          </main>
        </div>
      </div>

      {showSuccessPopup ? (
        <div className="create-truck-successPopup-overlay">
          <div className="create-truck-successPopup">
            <div className="create-truck-successPopup-icon">
              ✓
            </div>

            <h3>
              {t("success.createdTitle", {
                defaultValue:
                  "Camião criado",
              })}
            </h3>

            <p>
              {t("success.created", {
                defaultValue:
                  "Camião criado com sucesso.",
              })}
            </p>

            <button
              type="button"
              className="create-truck-successPopup-btn"
              onClick={() => navigate("/trucks")}
            >
              {t("buttons.ok", {
                defaultValue: "OK",
              })}
            </button>
          </div>
        </div>
      ) : null}

      {viewerFile && viewerUrl ? (
        <div
          className="create-truck-viewerOverlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setViewerFile(null)}
        >
          <div
            className="create-truck-viewerModal"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="create-truck-viewerHeader">
              <span
                className="create-truck-viewerTitle"
                title={viewerFile.title}
              >
                {viewerFile.title}
              </span>

              <button
                type="button"
                className="create-truck-viewerClose"
                aria-label="Fechar visualizador"
                title="Fechar"
                onClick={() => setViewerFile(null)}
              >
                ×
              </button>
            </div>

            <div className="create-truck-viewerBody">
              {isPdfFile(viewerFile.file) ? (
                <iframe
                  title={
                    viewerFile.title ||
                    "document"
                  }
                  src={viewerUrl}
                  className="create-truck-viewerFrame"
                />
              ) : isImageFile(viewerFile.file) ? (
                <img
                  src={viewerUrl}
                  alt={
                    viewerFile.title ||
                    "document"
                  }
                  className="create-truck-viewerImage"
                />
              ) : (
                <div className="create-truck-viewerUnsupported">
                  <FaFileAlt />

                  <p>
                    {viewerFile.title}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CreateTruck;